import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { NAV_SECTIONS } from "./navConfig";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/context/AuthContext";
import { initials } from "@/lib/format";
import { cn } from "@/lib/utils";
import { ChevronsLeft, ChevronsRight, LogOut, ChevronDown, Plus } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export function SidebarContent({ collapsed, onToggle, onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-col bg-[#07131A] contaha-grid">
      {/* Logo */}
      <div className={cn("flex h-16 items-center border-b border-[#223139]/60", collapsed ? "justify-center px-2" : "justify-between px-5")}>
        {collapsed ? <Logo showText={false} light /> : <Logo light />}
        {!collapsed && onToggle && (
          <button
            onClick={onToggle}
            data-testid="sidebar-collapse-btn"
            className="hidden lg:flex h-7 w-7 items-center justify-center rounded-md text-[#8B9698] hover:bg-[#0E1A21] hover:text-white transition-colors"
          >
            <ChevronsLeft className="h-4 w-4" />
          </button>
        )}
      </div>

      {collapsed && onToggle && (
        <button
          onClick={onToggle}
          className="hidden lg:flex mx-auto mt-2 h-7 w-7 items-center justify-center rounded-md text-[#8B9698] hover:bg-[#0E1A21] hover:text-white transition-colors"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
      )}

      {/* Primary CTA */}
      <div className={cn("px-3 pt-4", collapsed && "px-2")}>
        <button
          onClick={() => { onNavigate?.(); navigate("/consultation/new"); }}
          data-testid="sidebar-new-consultation"
          className={cn(
            "flex w-full items-center justify-center gap-2 rounded-lg bg-[#00A85A] py-2.5 text-sm font-bold text-white shadow-[0_4px_14px_rgba(0,168,90,0.35)] transition-colors hover:bg-[#008F4D]",
            collapsed && "px-0"
          )}
        >
          <Plus className="h-4 w-4" strokeWidth={2.5} />
          {!collapsed && "New Consultation"}
        </button>
      </div>

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto thin-scroll px-3 py-4">
        {NAV_SECTIONS.map((section) => {
          const items = section.items.filter((it) => !it.adminOnly || user?.is_admin);
          if (items.length === 0) return null;
          return (
          <div key={section.label} className="mb-5">
            {!collapsed && (
              <div className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#4E5F68]">
                {section.label}
              </div>
            )}
            <div className="space-y-0.5">
              {items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={({ isActive }) =>
                    cn(
                      "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                      collapsed && "justify-center px-0",
                      isActive
                        ? "bg-[#00A85A]/12 text-white"
                        : "text-[#8B9698] hover:bg-[#0E1A21] hover:text-white"
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-[#00A85A]" />}
                      <item.icon
                        className={cn("h-[18px] w-[18px] shrink-0", isActive ? "text-[#00A85A]" : "")}
                        strokeWidth={2}
                      />
                      {!collapsed && <span className="truncate">{item.label}</span>}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        );
        })}
      </nav>

      {/* Profile */}
      <div className="border-t border-[#223139]/60 p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="sidebar-profile-btn"
              className={cn(
                "flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-[#0E1A21]",
                collapsed && "justify-center"
              )}
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#00A85A] text-xs font-bold text-white overflow-hidden">
                {user?.avatar ? <img src={user.avatar} alt="" className="h-full w-full object-cover" /> : initials(user?.name)}
              </div>
              {!collapsed && (
                <>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-white">{user?.name}</div>
                    <div className="truncate text-xs text-[#8B9698]">{user?.clinic_name}</div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-[#8B9698]" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-56">
            <DropdownMenuLabel className="truncate">
              <div className="font-semibold">{user?.name}</div>
              <div className="text-xs font-normal text-[#8B9698]">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="logout-btn" className="text-red-500 focus:text-red-500">
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
